(() => {
  if (customElements.get("delivery-24h-page")) return;

  class Delivery24HPage extends HTMLElement {
    connectedCallback() {
      this.config = this.readConfig();
      this.grid = this.querySelector("[data-24h-grid]");
      this.skeletonGrid = this.querySelector("[data-24h-skeleton-grid]");
      this.resultCount = this.querySelector("[data-24h-result-count]");
      this.status = this.querySelector("[data-24h-status]");
      this.empty = this.querySelector("[data-24h-empty]");
      this.spinner = this.querySelector("[data-24h-spinner]");
      this.retryButton = this.querySelector("[data-24h-retry]");
      this.sentinel = this.querySelector("[data-24h-sentinel]");
      this.category = "cases";
      this.nextUrl = this.config.initialNextUrl || "";
      this.seen = new Set();
      this.requestSequence = 0;
      this.controller = new AbortController();
      this.loading = false;
      this.retryReplace = false;
      this.categoryCache = new Map();
      this.categoryCountController = new AbortController();

      this.bindEvents();
      this.initializeProducts().finally(() => {
        this.observeSentinel();
        this.loadCategoryCounts();
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.categoryCountController?.abort();
      this.observer?.disconnect();
    }

    readConfig() {
      const element = this.querySelector("[data-24h-config]");
      if (!element) return { strings: {}, sources: {} };
      try {
        return JSON.parse(element.textContent);
      } catch (error) {
        console.error("Invalid 24H Delivery configuration", error);
        return { strings: {}, sources: {} };
      }
    }

    bindEvents() {
      this.querySelectorAll("[data-24h-category]").forEach((button) => {
        button.addEventListener("click", () => {
          const category = button.getAttribute("data-24h-category");
          if (!category || category === this.category) return;
          this.category = category;
          this.updateActiveCategory(button);
          if (this.restoreCategoryState(category)) return;
          this.loadProducts({ replace: true });
        });
      });

      this.retryButton?.addEventListener("click", () => {
        this.loadProducts({ replace: this.retryReplace });
      });

      this.grid?.addEventListener("click", (event) => {
        const addButton = event.target.closest("[data-24h-add]");
        if (addButton) {
          event.preventDefault();
          this.addToCart(addButton);
          return;
        }

        if (event.target.closest("a, button, input, select, textarea, label")) {
          return;
        }
        const card = event.target.closest("[data-24h-card]");
        if (card?.dataset.productPath) {
          window.location.assign(card.dataset.productPath);
        }
      });
    }

    updateActiveCategory(activeButton) {
      this.querySelectorAll("[data-24h-category]").forEach((button) => {
        const active = button === activeButton;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", String(active));
      });
    }

    cacheCategoryState(category = this.category) {
      if (!this.grid || category !== this.category) return;
      const cards = [...this.grid.children]
        .filter((card) => card.matches("[data-24h-card]"))
        .map((card) => card.cloneNode(true));

      this.categoryCache.set(category, {
        cards,
        nextUrl: this.nextUrl,
        seen: [...this.seen],
      });
    }

    restoreCategoryState(category) {
      const cached = this.categoryCache.get(category);
      if (!cached || !this.grid) return false;

      this.controller?.abort();
      this.controller = null;
      this.requestSequence += 1;
      this.loading = false;
      this.retryReplace = false;
      this.nextUrl = cached.nextUrl || "";
      this.seen = new Set(cached.seen);
      this.grid.replaceChildren(
        ...cached.cards.map((card) => card.cloneNode(true)),
      );

      this.retryButton.hidden = true;
      this.spinner.hidden = true;
      this.hideStatus();
      this.hideGridSkeleton();
      this.updateResults();
      if (this.grid.children.length) this.hideEmpty();
      else this.showEmpty();
      this.continueLoadingIfNeeded();
      return true;
    }

    async initializeProducts() {
      if (!this.grid) return;
      const category = this.category;
      const sequence = this.requestSequence;
      const controller = this.controller || new AbortController();
      this.controller = controller;
      const initialCards = [...this.grid.querySelectorAll("[data-24h-card]")];
      this.grid.replaceChildren();
      this.showGridSkeleton();

      try {
        const eligibleCards = await this.filterEligibleCards(initialCards, {
          signal: controller.signal,
        });
        if (!this.isCurrentRequest(sequence, category, controller)) return;
        this.appendCards(eligibleCards);
        this.updateResults();

        if (this.grid.children.length < 12 && this.nextUrl) {
          await this.loadProducts({ replace: false });
        } else if (!this.grid.children.length) {
          this.showEmpty();
        } else {
          this.hideEmpty();
        }
        if (!this.isCurrentRequest(sequence, category, controller)) return;
        this.cacheCategoryState(category);
      } catch (error) {
        if (
          error?.name === "AbortError" ||
          !this.isCurrentRequest(sequence, category, controller)
        ) {
          return;
        }
        console.error("24H Delivery availability check failed", error);
        this.showStatus(
          this.config.strings?.serviceUnavailable ||
            "Next-day delivery is temporarily unavailable.",
        );
        this.showEmpty();
      } finally {
        if (this.isCurrentRequest(sequence, category, controller)) {
          this.loading = false;
          this.spinner.hidden = true;
          this.hideGridSkeleton();
        }
      }
    }

    isCurrentRequest(sequence, category, controller = this.controller) {
      return (
        sequence === this.requestSequence &&
        category === this.category &&
        controller === this.controller &&
        !controller?.signal.aborted
      );
    }

    availabilityClient() {
      const client = window.Torras24HAvailability;
      if (!client?.configured) {
        const error = new Error(
          this.config.strings?.serviceUnavailable ||
            "Next-day delivery is temporarily unavailable.",
        );
        error.code = "not_configured";
        throw error;
      }
      return client;
    }

    async loadCategoryCounts() {
      try {
        const client = this.availabilityClient();
        for (const [category] of Object.entries(this.config.sources || {})) {
          let requestUrl = this.sourceUrl(category);
          const visitedUrls = new Set();
          const variantIds = new Set();

          while (requestUrl && !visitedUrls.has(requestUrl)) {
            visitedUrls.add(requestUrl);
            const data = await this.fetchFragment(
              requestUrl,
              this.categoryCountController.signal,
            );
            data.cards.forEach((card) => {
              if (card?.dataset?.variantId) {
                variantIds.add(card.dataset.variantId);
              }
            });
            requestUrl = data.nextUrl
              ? new URL(data.nextUrl, window.location.origin).toString()
              : "";
          }

          const quantities = await client.check([...variantIds], {
            signal: this.categoryCountController.signal,
          });
          const count = [...quantities.values()].filter(
            (quantity) => quantity > 0,
          ).length;
          const element = this.querySelector(
            `[data-24h-category-count="${category}"]`,
          );
          if (element) element.textContent = String(count);
        }
      } catch (error) {
        if (error?.name === "AbortError") return;
        console.warn("24H Delivery category counts unavailable", error);
      }
    }

    async filterEligibleCards(cards, options = {}) {
      const candidateCards = cards.filter(
        (card) => card?.dataset?.variantId,
      );
      if (!candidateCards.length) return [];
      const quantities = await this.availabilityClient().check(
        candidateCards.map((card) => card.dataset.variantId),
        options,
      );
      return candidateCards.filter((card) => {
        const quantity =
          quantities.get(String(card.dataset.variantId || "")) || 0;
        card.dataset.delivery24hQuantity = String(quantity);
        return quantity > 0;
      });
    }

    observeSentinel() {
      if (!this.sentinel || !("IntersectionObserver" in window)) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (
            entries.some((entry) => entry.isIntersecting) &&
            this.nextUrl &&
            !this.loading
          ) {
            this.loadProducts({ replace: false });
          }
        },
        { rootMargin: "520px 0px" },
      );
      this.observer.observe(this.sentinel);
    }

    sentinelWithinPreloadRange() {
      if (!this.sentinel) return false;
      const rect = this.sentinel.getBoundingClientRect();
      const preloadMargin = 520;
      return (
        rect.bottom >= -preloadMargin &&
        rect.top <= window.innerHeight + preloadMargin
      );
    }

    continueLoadingIfNeeded() {
      window.requestAnimationFrame(() => {
        if (
          this.nextUrl &&
          !this.loading &&
          this.sentinelWithinPreloadRange()
        ) {
          this.loadProducts({ replace: false });
        }
      });
    }

    sourceUrl(category = this.category) {
      const source = this.config.sources?.[category];
      if (!source?.url) return "";

      const url = new URL(source.url, window.location.origin);
      url.searchParams.delete("page");
      url.searchParams.set("view", source.view || "24h-delivery");
      return url.toString();
    }

    async fetchFragment(url, signal) {
      const response = await fetch(url, {
        headers: { Accept: "text/html" },
        signal,
      });
      if (!response.ok) {
        throw new Error(
          this.config.strings?.loadError ||
            "We couldn’t load the products. Please try again.",
        );
      }

      const documentFragment = new DOMParser().parseFromString(
        await response.text(),
        "text/html",
      );
      const fragment = documentFragment.querySelector("[data-24h-fragment]");
      if (!fragment) {
        throw new Error(
          this.config.strings?.loadError ||
            "We couldn’t load the products. Please try again.",
        );
      }

      return {
        cards: [
          ...fragment.querySelectorAll(
            "[data-24h-fragment-cards] > [data-24h-card]",
          ),
        ],
        nextUrl: fragment.dataset.nextUrl || "",
      };
    }

    async loadProducts({ replace }) {
      if (!replace && (this.loading || !this.nextUrl)) return;

      if (replace) {
        this.controller?.abort();
        this.controller = new AbortController();
      } else if (!this.controller || this.controller.signal.aborted) {
        this.controller = new AbortController();
      }

      const category = this.category;
      const controller = this.controller;
      const sequence = ++this.requestSequence;
      let requestUrl = replace ? this.sourceUrl(category) : this.nextUrl;
      if (!requestUrl) {
        if (replace && this.isCurrentRequest(sequence, category, controller)) {
          this.grid?.replaceChildren();
          this.seen.clear();
          this.nextUrl = "";
          this.loading = false;
          this.spinner.hidden = true;
          this.updateResults();
          this.showEmpty();
          this.hideGridSkeleton();
        }
        return;
      }

      this.loading = true;
      this.retryButton.hidden = true;
      this.spinner.hidden = false;
      this.hideStatus();
      if (replace) this.showGridSkeleton();

      try {
        let firstResponse = true;
        const visitedUrls = new Set();

        while (requestUrl && !visitedUrls.has(requestUrl)) {
          visitedUrls.add(requestUrl);
          const data = await this.fetchFragment(requestUrl, controller.signal);
          if (!this.isCurrentRequest(sequence, category, controller)) return;
          const eligibleCards = await this.filterEligibleCards(data.cards, {
            signal: controller.signal,
          });
          if (!this.isCurrentRequest(sequence, category, controller)) return;

          if (replace && firstResponse) {
            this.grid.replaceChildren();
            this.seen.clear();
          }
          firstResponse = false;

          const added = this.appendCards(eligibleCards);
          this.nextUrl = data.nextUrl;

          if (added > 0 || !this.nextUrl) break;
          requestUrl = new URL(this.nextUrl, window.location.origin).toString();
        }

        if (!this.isCurrentRequest(sequence, category, controller)) return;
        this.updateResults();
        if (this.grid.children.length) this.hideEmpty();
        else this.showEmpty();
        this.cacheCategoryState(category);
      } catch (error) {
        if (
          error?.name === "AbortError" ||
          !this.isCurrentRequest(sequence, category, controller)
        ) {
          return;
        }
        console.error("24H Delivery fragment request failed", error);
        this.retryReplace = replace;
        this.retryButton.hidden = false;
        this.showStatus(
          error?.message ||
            this.config.strings?.loadError ||
            "We couldn’t load the products. Please try again.",
        );
      } finally {
        if (this.isCurrentRequest(sequence, category, controller)) {
          this.loading = false;
          this.spinner.hidden = true;
          this.hideGridSkeleton();
          this.continueLoadingIfNeeded();
        }
      }
    }

    showGridSkeleton() {
      if (!this.grid || !this.skeletonGrid) return;
      this.grid.hidden = true;
      this.grid.setAttribute("aria-busy", "true");
      this.skeletonGrid.hidden = false;
    }

    hideGridSkeleton() {
      if (!this.grid || !this.skeletonGrid) return;
      this.skeletonGrid.hidden = true;
      this.grid.hidden = false;
      this.grid.removeAttribute("aria-busy");
    }

    appendCards(cards) {
      const fragment = document.createDocumentFragment();
      let added = 0;

      cards.forEach((card) => {
        const variantId = String(card.dataset.variantId || "");
        if (!variantId || this.seen.has(variantId)) return;
        this.seen.add(variantId);
        fragment.appendChild(document.importNode(card, true));
        added += 1;
      });

      this.grid.appendChild(fragment);
      return added;
    }

    updateResults() {
      const count = this.grid?.querySelectorAll("[data-24h-card]").length || 0;
      if (this.resultCount) {
        const template =
          this.config.strings?.results || "{{ count }} products";
        this.resultCount.textContent = template
          .replace(/__COUNT__/g, String(count))
          .replace(/\{\{\s*count\s*\}\}/g, String(count));
      }
    }

    async addToCart(button) {
      if (button.disabled) return;
      const variantId = button.dataset.variantId;
      if (!variantId) return;

      const cart =
        document.querySelector("cart-notification") ||
        document.querySelector("cart-drawer");
      const formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", "1");
      if (cart?.getSectionsToRender) {
        formData.append(
          "sections",
          cart
            .getSectionsToRender()
            .map((section) => section.id)
            .join(","),
        );
        formData.append("sections_url", window.location.pathname);
        cart.setActiveElement?.(button);
      }

      button.disabled = true;
      button.classList.add("is-loading");
      this.hideStatus();

      try {
        const response = await fetch(this.config.cartAddUrl, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: formData,
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(
            data.description ||
              this.config.strings?.addGenericError ||
              "We couldn’t add this product. Please try again.",
          );
        }

        if (cart?.renderContents) {
          cart.classList.remove("is-empty");
          cart.renderContents(data);
        }
      } catch (error) {
        console.error("24H Delivery add to cart failed", error);
        this.showStatus(
          error?.message ||
            this.config.strings?.addGenericError ||
            "We couldn’t add this product. Please try again.",
        );
      } finally {
        button.disabled = false;
        button.classList.remove("is-loading");
      }
    }

    showStatus(message) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.hidden = false;
    }

    hideStatus() {
      if (this.status) this.status.hidden = true;
    }

    showEmpty() {
      if (this.empty) this.empty.hidden = false;
    }

    hideEmpty() {
      if (this.empty) this.empty.hidden = true;
    }
  }

  customElements.define("delivery-24h-page", Delivery24HPage);
})();
